/**
 * Rainbow Six Siege map roster (current 2026 rotation).
 * Each map: sites, common attacker spawns, common entry options, defender setup hints.
 * Data is flavour / tactical guidance — not exhaustive.
 * Add new maps by appending an entry.
 */

export const MAPS = [
  {
    id: "bank",
    name: "Bank",
    sites: ["CEO Office / Exec Lounge", "Open Area / Vault", "Lockers / CCTV", "Basement Archives"],
    attackerSpawns: ["Boulevard", "Alley", "Back Alley", "Park"],
    attackerEntries: [
      "Breach garage shutter and push vehicle depot",
      "Vault in through front lobby windows",
      "Rappel to CEO Office roof and drop hatches",
      "Back Alley kitchen window for quick basement access",
      "East Stairs after clearing front lobby",
      "Skylight ladder onto rooftop for vertical play",
    ],
    defenderSetups: [
      "Anchor near the reinforced site wall and deny the main entry",
      "Roam basement/upstairs to cut flank timing",
      "Hold the long lobby hallway angle for first pick",
    ],
  },
  {
    id: "border",
    name: "Border",
    sites: ["Armory Lockers / Workshop", "Tellers / Bathroom", "Ventilation / Archives", "Customs Inspection / Supply"],
    attackerSpawns: ["Bus Entrance", "East Balcony", "Valley", "Hills / Border Gate"],
    attackerEntries: [
      "Vault Bus roof window and drop into offices",
      "Rappel Customs Office balcony and breach",
      "Push ventilation hatch from Valley rooftop",
      "Main entrance front doors after cam clearing",
      "East stairs after clearing Armory hallway",
    ],
    defenderSetups: [
      "Deny Armory wall with Bandit/Kaid and hold Tellers cross",
      "Anchor Bathroom with Mira/Azami for off-angle",
      "Roam upstairs to punish rappel setups",
    ],
  },
  {
    id: "chalet",
    name: "Chalet",
    sites: ["Kitchen / Trophy", "Library / Games Room", "Master Bedroom / Office", "Wine Cellar / Snowmobile"],
    attackerSpawns: ["Front Yard", "Helipad", "Lakeside", "Campfire"],
    attackerEntries: [
      "Breach Garage door for Wine Cellar pressure",
      "Dining Room windows for Trophy / Kitchen executes",
      "Helipad main entrance for Library rush",
      "Rappel Master Bedroom balcony",
      "Lakeside windows into Basement rotations",
      "Bar windows to force defender rotates",
    ],
    defenderSetups: [
      "Reinforce Fireplace wall and hold Wine Stock angle",
      "Anchor Snowmobile with Mute jammers on hatches",
      "Roam Library 2F to punish main pushes",
    ],
  },
  {
    id: "clubhouse",
    name: "Clubhouse",
    sites: ["CCTV / Cash Room", "Gym / Bedroom", "Bar / Stock Room", "Church / Arsenal"],
    attackerSpawns: ["Construction", "Compound", "Main Gate", "River"],
    attackerEntries: [
      "Blue Stairs into Cash Room via piano",
      "Skylight hatch onto 2F gym/bedroom",
      "Construction site front door + garage",
      "Rappel Gym balcony window",
      "Church side windows for Basement pressure",
      "CCTV hatch from 2F central hall",
    ],
    defenderSetups: [
      "Reinforce CCTV wall + Cash Room garage wall",
      "Anchor Bar with Castle barricades",
      "Roam Church stairs to trade kills",
    ],
  },
  {
    id: "coastline",
    name: "Coastline",
    sites: ["Hookah Lounge / Billiards", "Kitchen / Service", "Penthouse / Theater", "Blue Bar / Sunrise Bar"],
    attackerSpawns: ["Main Gate", "Beach", "Pool", "Rooftop"],
    attackerEntries: [
      "VIP staircase to Hookah ceiling hatches",
      "Pool windows into Kitchen",
      "Rappel Penthouse balconies",
      "Rooftop skylight drop into Theater",
      "Back staircase via service for surprise flank",
    ],
    defenderSetups: [
      "Reinforce Hookah vents + hold Billiards sightline",
      "Anchor Kitchen with Maestro Evil Eyes",
      "Roam Aquarium / VIP corridor",
    ],
  },
  {
    id: "consulate",
    name: "Consulate",
    sites: ["Piano Room / Lobby", "Meeting Room / Consul Office", "Archives / Cafeteria", "Tellers / Garage"],
    attackerSpawns: ["Front Entrance", "Garage Exterior", "Courtyard", "Side Entrance"],
    attackerEntries: [
      "Main gate front doors + staircase",
      "Garage roll-up breach for Archives push",
      "Rappel Consul balcony",
      "Courtyard windows into Piano",
      "Skylight hatch onto 2F server room",
    ],
    defenderSetups: [
      "Reinforce Consul wall + hold side hallway",
      "Anchor Cafeteria / Tellers cross angle",
      "Roam 2F Public Bathrooms for flanks",
    ],
  },
  {
    id: "kafe",
    name: "Kafe Dostoyevsky",
    sites: ["Cocktail / Bar", "Reading Room / Fireplace", "Kitchen Service / Cooking", "Mining Room / Train"],
    attackerSpawns: ["Park", "Christmas Market", "Side Alley", "Restaurant Entrance"],
    attackerEntries: [
      "Front lobby doors + main staircase",
      "Park windows into Christmas Market drop",
      "Rappel Bar 3F balcony for vertical",
      "Back alley through Bakery",
      "Service kitchen door breach",
    ],
    defenderSetups: [
      "Reinforce Bar wall + White Stairs lockdown",
      "Anchor Reading Room with Mira window",
      "Roam 1F kitchens to counter rush",
    ],
  },
  {
    id: "lair",
    name: "Lair",
    sites: ["Corridor / Bar", "Armoury / Boardroom", "Server / Archive", "Nuke / Control Room"],
    attackerSpawns: ["Entrance", "Mountain Ridge", "Server Vent", "Rooftop"],
    attackerEntries: [
      "Main entrance blast doors",
      "Server vent drop via rooftop",
      "Rappel Armoury windows",
      "Back entrance via Mountain Ridge",
      "Nuke room hatch from control hallway",
    ],
    defenderSetups: [
      "Reinforce central server wall + hold Corridor",
      "Anchor Boardroom with trap-heavy setup",
      "Roam upper catwalks for picks",
    ],
  },
  {
    id: "nighthaven_labs",
    name: "Nighthaven Labs",
    sites: ["Server / Dev Room", "Meeting Room / Labs", "Warehouse / Shipping", "Security / Data Vault"],
    attackerSpawns: ["Helipad", "Loading Dock", "Front Entrance", "Courtyard"],
    attackerEntries: [
      "Helipad roof rappel + skylights",
      "Loading dock garage breach",
      "Front lobby windows",
      "Courtyard rappel into meeting rooms",
      "Data vault side corridor push",
    ],
    defenderSetups: [
      "Reinforce Lab glass walls + hold hallway",
      "Anchor Data Vault with Azami barriers",
      "Roam Warehouse rafters for flank",
    ],
  },
  {
    id: "oregon",
    name: "Oregon",
    sites: ["Kitchen / Dining", "Kids Room / Dorms", "Laundry / Supply", "Meeting Hall / Tower"],
    attackerSpawns: ["Construction", "Main Bridge", "Street", "Junkyard"],
    attackerEntries: [
      "Main house front door + Meeting breach",
      "Tower rappel for vertical hatches",
      "Kitchen/Dining window breach",
      "Back kitchen hatch drop onto Laundry",
      "Dorms 2F rappel",
    ],
    defenderSetups: [
      "Reinforce Meeting Hall walls + Tower hatches",
      "Anchor Dorms with trap/Mira setup",
      "Roam Kitchen upstairs to trade",
    ],
  },
  {
    id: "skyscraper",
    name: "Skyscraper",
    sites: ["Bedroom / Tea Room", "Exhibition / Office", "Geisha Room / Bathroom", "Kitchen / BBQ"],
    attackerSpawns: ["Side Garden", "Helipad", "Back Garden", "Piano Stairs"],
    attackerEntries: [
      "Helipad rappel + skylight hatches",
      "Side Garden windows into Tea Room",
      "BBQ breach wall for Kitchen push",
      "Main entrance lobby + Tatami",
      "Back Garden into Geisha Room",
    ],
    defenderSetups: [
      "Reinforce Bedroom walls + Tea Room cross",
      "Anchor Exhibition with Maestro cams",
      "Roam 1F to punish long approaches",
    ],
  },
  {
    id: "villa",
    name: "Villa",
    sites: ["Trophy / Statuary", "Aviator / Games", "Living / Library", "Kitchen / Dining"],
    attackerSpawns: ["Main Gate", "Astronomy", "Ruins", "Front Yard"],
    attackerEntries: [
      "Astronomy side rappel for Aviator",
      "Front garden windows to Trophy",
      "Back garden kitchen breach",
      "Second floor balcony drop into Library",
      "Ruins rappel into Dining",
    ],
    defenderSetups: [
      "Reinforce Statuary wall + Trophy cross",
      "Anchor Library with Maestro covering stairs",
      "Roam Astronomy side for map control",
    ],
  },
  {
    id: "emerald_plains",
    name: "Emerald Plains",
    sites: ["Bar / Lobby", "Cigar Lounge / Lounge", "Kitchen / Dining", "Gym / Locker Room"],
    attackerSpawns: ["Gazebo", "Car Park", "Golf Course", "Delivery"],
    attackerEntries: [
      "Main lobby doors + Bar breach",
      "Rappel 2F balconies",
      "Gym side windows",
      "Delivery garage roll-up",
      "Roof skylight drops into site",
    ],
    defenderSetups: [
      "Reinforce Bar wall + Lobby window shutters",
      "Anchor Cigar Lounge with trap traffic",
      "Roam Kitchen upstairs for flanks",
    ],
  },
  {
    id: "fortress",
    name: "Fortress",
    sites: ["Bathroom / Office", "Briefing / Dorms", "Commander / Tower", "Red Stairs / Service"],
    attackerSpawns: ["Village", "Cliffside", "Helipad", "Commanders Hall"],
    attackerEntries: [
      "Main gate doors + courtyard",
      "Helipad roof rappel",
      "Village side windows",
      "Cliffside towers rappel",
      "Service back entrance",
    ],
    defenderSetups: [
      "Reinforce Commander walls + hold Red Stairs",
      "Anchor Briefing with Mira window",
      "Roam rooftops to counter rappel",
    ],
  },
  {
    id: "hereford",
    name: "Hereford Base",
    sites: ["Kitchen / Garage", "Dorms / Bathroom", "Attic / Master", "Basement / Storage"],
    attackerSpawns: ["Farmland", "Parking", "Main Road", "Backyard"],
    attackerEntries: [
      "Garage roll-up door breach",
      "Main door front porch",
      "Attic rappel skylight",
      "Bathroom side windows",
      "Kitchen back window",
    ],
    defenderSetups: [
      "Reinforce Attic floors + hatches",
      "Anchor Basement with traps",
      "Roam Kitchen to cut spawn-peek trades",
    ],
  },
  {
    id: "house",
    name: "House",
    sites: ["Bedroom / Bathroom", "Workshop / Garage", "Kitchen / Dining", "Boys Room / Girls Room"],
    attackerSpawns: ["Driveway", "Backyard", "Side Alley", "Front Porch"],
    attackerEntries: [
      "Garage front breach",
      "Back porch windows",
      "Rappel 2F bedrooms",
      "Side alley kitchen window",
      "Roof hatches on attic",
    ],
    defenderSetups: [
      "Reinforce Master bedroom walls",
      "Anchor Kitchen/Dining with traps",
      "Roam 1F for flanks",
    ],
  },
  {
    id: "outback",
    name: "Outback",
    sites: ["Convenience Store / Mechanic", "Games Room / Piano", "Laundry / Dorms", "Party Room / Compressor"],
    attackerSpawns: ["Scrapyard", "Party Pool", "Roadside", "Service Road"],
    attackerEntries: [
      "Mechanic garage roll-up",
      "Pool side windows",
      "Rappel Party Room balcony",
      "Store front lobby doors",
      "Roof hatches on 2F",
    ],
    defenderSetups: [
      "Reinforce Mechanic walls + hold Compressor",
      "Anchor Games Room with cams",
      "Roam Piano stairs",
    ],
  },
  {
    id: "plane",
    name: "Presidential Plane",
    sites: ["Cockpit / Meeting", "Luggage / Cargo", "Work Office / Comms", "Lounge / Bar"],
    attackerSpawns: ["Wings", "Tarmac", "Maintenance", "Catering Truck"],
    attackerEntries: [
      "Wing emergency exits",
      "Cargo bay rear door",
      "Main cabin front hatch",
      "Catering side door",
      "Roof hatch rappel",
    ],
    defenderSetups: [
      "Reinforce Work Office walls",
      "Anchor Cockpit narrow angles",
      "Roam Cargo to rotate fast",
    ],
  },
  {
    id: "stadium_bravo",
    name: "Stadium Bravo",
    sites: ["Press Lounge / Lobby", "Broadcast / Studio", "VIP / Skybox", "Lockers / Tunnel"],
    attackerSpawns: ["Field", "Concourse", "Upper Deck", "Media Entrance"],
    attackerEntries: [
      "Main concourse doors",
      "Upper deck rappel",
      "Field tunnel push",
      "Studio side windows",
      "Skybox hatch drop",
    ],
    defenderSetups: [
      "Reinforce Studio glass + hold VIP angles",
      "Anchor Lockers with traps",
      "Roam concourse for long picks",
    ],
  },
  {
    id: "theme_park",
    name: "Theme Park",
    sites: ["Day Care / Throne Room", "Office / Bunk", "Bumper Cars / Initiation", "Lab / Storage"],
    attackerSpawns: ["Main Street", "Bumper Car Exterior", "Side Street", "Parade"],
    attackerEntries: [
      "Front initiation doors",
      "Rappel throne room windows",
      "Office back door breach",
      "Bumper Cars side windows",
      "Roof hatches into Lab",
    ],
    defenderSetups: [
      "Reinforce Throne Room walls + hold Day Care",
      "Anchor Bumper Cars with traps",
      "Roam Lobby for map control",
    ],
  },
  {
    id: "tower",
    name: "Tower",
    sites: ["Tea Room / Bar", "Observation / Mezzanine", "Birthday / Bedroom", "Kitchen / Dining"],
    attackerSpawns: ["Helipad", "Ground Floor Lobby", "Rooftop", "Cafe"],
    attackerEntries: [
      "Helipad drop via skylight",
      "Main lobby elevators",
      "Rooftop rappel down windows",
      "Mezzanine side windows",
      "Birthday room balcony",
    ],
    defenderSetups: [
      "Reinforce Observation walls + hold Bar",
      "Anchor Birthday room with cams",
      "Roam Mezzanine for verticality",
    ],
  },
  {
    id: "yacht",
    name: "Yacht",
    sites: ["Engine / Cooler", "Casino / Submarine", "Navigation / Bar", "Kitchen / Dining"],
    attackerSpawns: ["Ice / Zodiac", "Helipad", "Submarine", "Snowmobile"],
    attackerEntries: [
      "Helipad rappel down",
      "Zodiac ice side windows",
      "Submarine engine hatch drop",
      "Navigation front doors",
      "Casino side windows",
    ],
    defenderSetups: [
      "Reinforce Engine walls + hold Cooler angle",
      "Anchor Navigation with Mira",
      "Roam Deck for long angles",
    ],
  },
];

// Generic fallbacks used if a map somehow has no entry data.
const GENERIC_SPAWNS = ["Main exterior spawn", "Side exterior spawn", "Rear exterior spawn", "Flank spawn"];
const GENERIC_ENTRIES = [
  "Breach a soft wall into the bomb site",
  "Rappel 2F windows and drop hatches",
  "Push main entrance after cam clearing",
  "Open a vertical angle from the floor above",
];
const GENERIC_SETUPS = [
  "Anchor near the bomb and deny main entry",
  "Roam to cut flank timing",
  "Hold a long sightline into a key hallway",
];

export const getMapById = (id) => MAPS.find((m) => m.id === id);

export const randomMap = () => MAPS[Math.floor(Math.random() * MAPS.length)];

export const getSpawnsFor = (map) =>
  map && map.attackerSpawns && map.attackerSpawns.length ? map.attackerSpawns : GENERIC_SPAWNS;

export const getEntriesFor = (map) =>
  map && map.attackerEntries && map.attackerEntries.length ? map.attackerEntries : GENERIC_ENTRIES;

export const getSetupsFor = (map) =>
  map && map.defenderSetups && map.defenderSetups.length ? map.defenderSetups : GENERIC_SETUPS;
